import { useCallback, useRef } from 'react';

/**
 * Capture the editor's CURRENT in-session content and hand the backend a URL to fetch it from.
 *
 * WHY THIS EXISTS — the document on disk is routinely behind what the doctor sees:
 *
 * OnlyOffice only writes back to our storage on a callback, and there are exactly two:
 * the session closing (status 2, which arrives ~5s AFTER the editor is torn down) and an
 * explicit forcesave (status 6). There is no periodic autosave to disk — `autoAssembly` is
 * disabled, and `customization.autosave` only governs the document server's own cache.
 *
 * Worse, keystrokes live in the BROWSER until the client ships them over the websocket
 * (~2-3s). A server-side forcesave issued inside that window has two ways to lose them:
 *   - the document server answers error 4 ("nothing pending") because it hasn't received
 *     them yet, which the backend used to read as "disk is already current"; or
 *   - the forcesave succeeds and assembles a file from what the document server has,
 *     reporting success while silently omitting the last few seconds of dictation.
 *
 * Both produce a signed report containing the bare template. `downloadAs` is the only
 * capture path that asks the EDITOR to assemble what is on screen right now, so it
 * includes changes the document server has never seen. Sign-off is irreversible and goes
 * to HIS, so it must use this path rather than trusting the file on disk.
 */
export const useInSessionCapture = (editorInstanceRef: React.MutableRefObject<any>) => {
  // Resolver for the in-flight downloadAs() round-trip (OO answers via the onDownloadAs event)
  const resolverRef = useRef<((url: string | null) => void) | null>(null);

  /** Wire into the editor's `events.onDownloadAs` — the answer to our downloadAs() call. */
  const handleDownloadAs = useCallback((event: any) => {
    const url = event?.data?.url || (typeof event?.data === 'string' ? event.data : null);
    resolverRef.current?.(url);
    resolverRef.current = null;
  }, []);

  /**
   * Resolves with a document-server URL holding the live content, or null if the editor
   * can't answer (not mounted, API unavailable, or it never fired the event). Callers must
   * treat null as "capture failed" — never as "there was nothing to capture".
   */
  const requestCurrentDocUrl = useCallback((timeoutMs = 10_000): Promise<string | null> => {
    return new Promise(resolve => {
      const editor = editorInstanceRef.current;
      if (!editor || typeof editor.downloadAs !== 'function') {
        resolve(null);
        return;
      }
      const timer = setTimeout(() => {
        resolverRef.current = null;
        resolve(null);
      }, timeoutMs);
      resolverRef.current = (url: string | null) => {
        clearTimeout(timer);
        resolve(url);
      };
      try {
        editor.downloadAs('docx');
      } catch {
        clearTimeout(timer);
        resolverRef.current = null;
        resolve(null);
      }
    });
  }, [editorInstanceRef]);

  return { requestCurrentDocUrl, handleDownloadAs };
};
